<template>
  <div v-if="currentPage" class="page-turner relative">
    <div class="button-container left hidden land:block"><button class="left" @click="previous" :disabled="currentPageIndex === 0">&lt;</button></div>
    <div class="button-container left block land:hidden absolute left-0 h-full flex items-center opacity-50 hover:opacity-100">
      <div><button class="left" @click="previous" :disabled="currentPageIndex === 0">&lt;</button></div>
    </div>
    <div class="page-container" v-if="pagesPerSpread === 1">
      <div v-if="currentPage" class="page single">
        <page-image v-show="!showMagnifier" :key="currentPage.id" :page="currentPage" :reveal="isRevealed(currentPage)" :max-height="maxImageHeight"
                    @load="onPageImageLoad">
          <page-link-areas :page="currentPage" />
        </page-image>
        <vue-magnifier :magStyle="getMagStyle(0)" :src="currentPage.pictureUrl" :src-large="currentPage.pictureUrl"></vue-magnifier>
      </div>
    </div>
    <div class="page-container flex-col sm:flex-row" v-if="pagesPerSpread === 2">
      <div v-if="currentPage" class="page double first">
        <page-image v-show="!showMagnifier" :key="currentPage.id" :page="currentPage" :reveal="isRevealed(currentPage)" :max-height="maxImageHeight"
                    @load="onPageImageLoad">
          <page-link-areas :page="currentPage" />
        </page-image>
        <vue-magnifier :magStyle="getMagStyle(0)" :src="currentPage.pictureUrl" :src-large="currentPage.pictureUrl"></vue-magnifier>
      </div>
      <div v-if="nextPage" class="page double second">
        <page-image v-show="!showMagnifier" :key="nextPage.id" :page="nextPage" :reveal="isRevealed(nextPage)" :max-height="maxImageHeight"
                    @load="onPageImageLoad">
          <page-link-areas :page="nextPage" />
        </page-image>
        <vue-magnifier :magStyle="getMagStyle(1)" :src="nextPage.pictureUrl" :src-large="nextPage.pictureUrl"></vue-magnifier>
      </div>
    </div>
    <link v-for="prefetchPage in prefetchPages" :key="prefetchPage.id" rel="prefetch" :href="prefetchPage.pictureUrl">
    <div class="button-container right hidden land:block"><button class="right" @click="next" :disabled="currentPageIndex >= pages.length - pagesPerSpread">&gt;</button></div>
    <div class="button-container right block land:hidden absolute right-0 h-full flex items-center opacity-50 hover:opacity-100">
      <div><button class="right" @click="next" :disabled="currentPageIndex >= pages.length - pagesPerSpread">&gt;</button></div>
    </div>
  </div>
  <div v-else>
    No pages yet
  </div>
</template>

<script>
import autolinker from 'autolinker'
import PageImage from './PageImage'
import PageLinkAreas from './PageLinkAreas'
import VueMagnifier from './VueMagnifier'
export default {
  props: ['pages', 'pagesPerSpread', 'currentPageIndex', 'setCurrentPageIndex', 'showMagnifyingGlass'],
  data() {
    return {
      maxImageHeight: null,
      maxImageWidth: null,
      pagesAlreadyFetchedById: {},
      loadedPageIds: {},

      // Ok here's how this works. The VueMagnifier component I downloaded wasn't versatile enough to allow any element
      // passed into it. But I want the placeholder behavior of PageImage. So basically I show PageImage, then when the
      // spread's images are loaded, I swap it out for the VueMagnifier component.
      doneLoadingImages: false,
    }
  },
  computed: {
    currentPage() {
      return this.pages[this.currentPageIndex]
    },
    nextPage() {
      if (this.pagesPerSpread !== 2) {
        return null
      }
      return this.pages[this.currentPageIndex + 1]
    },
    spreadPages() {
      return [this.currentPage, this.nextPage].filter(Boolean)
    },
    spreadLoaded() {
      return this.spreadPages.every(page => this.loadedPageIds[page.id])
    },
    prefetchPages() {
      const potentialPrefetchPageIndexes = [
        this.currentPageIndex + 1,
        this.currentPageIndex + 2,
      ]
      if (this.pagesPerSpread === 2) {
        potentialPrefetchPageIndexes.push(this.currentPageIndex + 3);
        potentialPrefetchPageIndexes.push(this.currentPageIndex + 4);
      }
      // Remove undefined values and pages already fetched.
      const prefetchPageIndexes = potentialPrefetchPageIndexes.filter(id => this.pages[id] && !this.pagesAlreadyFetchedById[id])
      // TODO: No side-effects in a computed property!
      // eslint-disable-next-line
      prefetchPageIndexes.forEach(id => this.pagesAlreadyFetchedById[id] = true)
      const pages = prefetchPageIndexes.map(id => this.pages[id])
      return pages
    },
    showMagnifier() {
      return this.showMagnifyingGlass && this.doneLoadingImages
    },
  },
  methods: {
    getMagStyle() {
      return {
        display: this.showMagnifier ? 'block' : 'none',
        width: this.maxImageWidth + 'px',
        height: this.maxImageHeight + 'px',
      }
    },
    // Pages without a thumbnail have nothing to show while waiting, so show them as soon as they load.
    isRevealed(page) {
      return this.spreadLoaded || !page.thumbnailUrl
    },
    onPageImageLoad(page) {
      this.$set(this.loadedPageIds, page.id, true)
      this.doneLoadingImages = this.spreadLoaded
      this.doOptimizedResize()
    },
    previous() {
      this.setCurrentPageIndex(this.currentPageIndex - this.pagesPerSpread)
    },
    next() {
      this.setCurrentPageIndex(this.currentPageIndex + this.pagesPerSpread)
    },
    arrowKeysListener(event) {
      if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
        return
      }
      if (event.code === 'ArrowLeft' && this.currentPageIndex > 0) {
        this.previous()
      } else if (event.code === 'ArrowRight' && this.currentPageIndex < this.pages.length - this.pagesPerSpread) {
        this.next()
      }
    },
    autolink(text) {
      return autolinker.link(text, {
        newWindow: true,
      })
    },
    doOptimizedResize() {
      const topElement = this.$el.querySelector('.page')
      if (!topElement) {
        return
      }
      // Adjustments means like padding and leeway.
      const adjustments = 10
      this.maxImageHeight = document.documentElement.clientHeight - topElement.getBoundingClientRect().top - adjustments
      if (document.documentElement.clientWidth < 640) {
        this.maxImageHeight = this.maxImageHeight / 2
      }

      if (this.showMagnifier) {
        const leftButton = this.$el.querySelector('.button-container.left')
        console.log('leftButton', leftButton)
        let buttonGutter = 0
        if (leftButton) {
          buttonGutter += leftButton.getBoundingClientRect().width
        }
        const rightButton = this.$el.querySelector('.button-container.right')
        if (rightButton) {
          buttonGutter += rightButton.getBoundingClientRect().width
        }
        const totalWidth = this.$el.getBoundingClientRect().width
          + parseFloat(window.getComputedStyle(this.$el).marginLeft, 10)
          + parseFloat(window.getComputedStyle(this.$el).marginRight, 10)
        console.log('totalWidth', totalWidth)
        console.log('buttonGutter', buttonGutter)
        this.maxImageWidth = (totalWidth - buttonGutter) / 2
        console.log('this.maxImageWidth', this.maxImageWidth)
      }
    },
  },
  watch: {
    currentPageIndex() {
      this.doneLoadingImages = this.spreadLoaded
    },
    showMagnifyingGlass() {
      this.doOptimizedResize()
    },
  },
  mounted() {
    window.addEventListener('keydown', this.arrowKeysListener)

    // Got this from https://developer.mozilla.org/en-US/docs/Web/Events/resize.
    // Here's what's happening. We use requestAnimationFrame to emit our customevent, and then
    // listen for that. We size once up front so PageImage's placeholder box doesn't start out at full width.
    var throttle = function(type, name, obj) {
        obj = obj || window
        var running = false
        var func = function() {
            if (running) { return }
            running = true
            requestAnimationFrame(function() {
                obj.dispatchEvent(new CustomEvent(name))
                running = false
            })
        }
        obj.addEventListener(type, func)
    }
    throttle('resize', 'optimizedResize')
    window.addEventListener('optimizedResize', this.doOptimizedResize)
    this.$nextTick(this.doOptimizedResize)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.arrowKeysListener)
    window.removeEventListener('optimizedResize', this.doOptimizedResize)
  },
  components: {
    PageImage,
    PageLinkAreas,
    VueMagnifier,
  },
}
</script>

<style lang="stylus" scoped>
.page-turner
  flex 1
  display flex
  align-items center
  margin 10px auto

  @media only screen and (max-width: 1200px)
    width 100%

  img
    max-width 100%

.page-container
  display flex
  flex 1
  min-width 0
  margin 0 auto

.page.single
  flex 1

  .page-image
    margin 0 auto

.page.double
  max-width 2200px
  flex 1

  &.first
    background-size cover
    padding 10px 0 0 20px

    @media only screen and (max-width: 1200px)
      padding: 0

    .page-image
      margin-left auto
      border-left 1px solid #cccccc
      border-top 1px solid #cccccc
      border-bottom 1px solid #cccccc

  &.second
    background-size cover
    padding 10px 20px 0 0

    @media only screen and (max-width: 1200px)
      padding 0

    .page-image
      margin-right auto
      border-right 1px solid #cccccc
      border-top 1px solid #cccccc
      border-bottom 1px solid #cccccc

  .credits
      visibility hidden
      background-color #ccc
      color #ffffff
      text-align left
      padding 10px
      margin-top 10px
      margin-bottom 20px

  &:hover
    .credits
      visibility visible

button
  border-radius 50px
  height 50px
  width 50px
  font-size 18px
  color white

  // @media only screen and (max-width: 1000px)
  //   height 35px
  //   width 35px
  //   font-size 1px

  &[disabled]
    visibility hidden

.button-container
  padding 5px
</style>
